/**
 * Owner-facing "upload a supporting document" control for a leave that is still
 * awaiting HR document review. Lets the employee respond to a "please update
 * your request" ask by attaching a new document from the leave detail modal.
 *
 * Backed by POST /api/v1/leaves/{id}/documents (owner-or-HR+). When the leave
 * had a prior document request, the upload re-notifies HR (server-side).
 */
import { useRef, useState } from 'react'

import { ApiError } from '../../api/client'
import { uploadDocument } from '../../api/documents'
import { UPLOAD_ACCEPT_ATTR } from '../../lib/upload-accept'
import { Button } from '../primitives/Button'
import { useToast } from '../primitives/Toast'

interface LeaveDocumentUploadProps {
  leaveId: string
  /** Called after a successful upload so the host can refresh the detail. */
  onUploaded: () => void | Promise<void>
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Upload failed.'
}

export function LeaveDocumentUpload({ leaveId, onUploaded }: LeaveDocumentUploadProps): JSX.Element {
  const { notify } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  async function handleFiles(fileList: FileList | null): Promise<void> {
    const files = fileList ? Array.from(fileList) : []
    if (files.length === 0) return
    setBusy(true)
    try {
      for (const file of files) {
        await uploadDocument(leaveId, file)
      }
      notify(files.length > 1 ? 'Documents uploaded' : 'Document uploaded', 'success')
      await onUploaded()
    } catch (err) {
      notify(`Upload failed — ${errorMessage(err)}`, 'error')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
        Need to add a document? Upload one to update your request.
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_ACCEPT_ATTR}
        multiple
        data-testid="leave-detail-document-upload-input"
        style={{ display: 'none' }}
        onChange={(e) => void handleFiles(e.target.files)}
      />
      <div>
        <Button
          variant="soft"
          size="sm"
          loading={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="leave-detail-document-upload"
        >
          Upload document
        </Button>
      </div>
    </div>
  )
}
